import { IncomeDTO, IncomeModel, IncomeType, OwnerModelDTO } from '../../api/useIncomeQuery';
import { getDefaultCustomBucketIndex } from '../../utils/getDefaultCustomBucketIndex';

export type OtherIncomeBody = Omit<OtherIncomeFormValues, never>;

export interface UpsertIncomeDTO extends OtherIncomeBody {
  clientId: string;
  id: string | null;
  incomeType: IncomeType;
}

export interface OtherIncomeFormValues {
  Name: string;
  Earner: string;
  Amount: number | null;
  StartAge: number | null | undefined;
  EndAge: number | null | undefined;
  EndAtRetirement: boolean | null;
  EndAtEndOfPlan: boolean | null;
  AccountEarmark: { Type: string }[];
  Inflation: number | null;
  IsTaxFree: boolean | null;
  OrionManagementStyleId: number | null;
  CustomBucketIndex: number | null;
  OwnerModel: OwnerModelDTO[];
}

const getEndAge = (income: IncomeModel) => {
  if (income.EndAtRetirement) {
    return null;
  }
  if (income.EndAtEndOfPlan) {
    return -1;
  }
  if (income.EndAge) {
    return income.EndAge;
  }
  return undefined;
};

const defaultValues = {
  Name: '',
  Amount: null,
  StartAge: undefined,
  EndAge: undefined,
  EndAtRetirement: false,
  EndAtEndOfPlan: false,
  AccountEarmark: [],
  Inflation: null,
  IsTaxFree: false,
  OrionManagementStyleId: null,
  CustomBucketIndex: null,
};

export const dataToForm = (income: IncomeModel | null, data: IncomeDTO): OtherIncomeFormValues => {
  const { Options: options } = data;

  if (!income) {
    return {
      ...defaultValues,
      Earner: options.OwnerOptions.People[0].Name,
      OwnerModel: [
        {
          Id: null,
          Name: options.OwnerOptions.People[0].Name,
          OwnerType: 0,
          Percentage: 1,
        },
      ],
    };
  }

  return {
    Name: income.Name,
    Earner: income.Earner,
    Amount: income.Amount,
    StartAge: income.StartAge,
    EndAge: getEndAge(income),
    EndAtRetirement: income.EndAtRetirement,
    EndAtEndOfPlan: income.EndAtEndOfPlan,
    AccountEarmark: income.AccountEarmark.map(earmark => ({ Type: earmark.Type })),
    Inflation: income.Inflation,
    IsTaxFree: income.IsTaxFree,
    OrionManagementStyleId: income.OrionManagementStyleId,
    CustomBucketIndex: getDefaultCustomBucketIndex({
      isAssetBucketingByManagementStyle: options.AssetBucketingByManagementStyle,
      customBucketIndex: income.CustomBucketIndex,
      orionManagementStyleId: income.OrionManagementStyleId,
      managementStylesOptions: options.ManagementStyles,
    }),
    OwnerModel: income.OwnerModel,
  };
};

export const formToBody = (values: OtherIncomeFormValues): OtherIncomeBody => ({
  Name: values.Name,
  Earner: values.Earner,
  Amount: values.Amount,
  StartAge: values.StartAge,
  EndAge: values.EndAtRetirement || values.EndAtEndOfPlan ? null : values.EndAge,
  EndAtRetirement: values.EndAtRetirement,
  EndAtEndOfPlan: values.EndAtEndOfPlan,
  AccountEarmark: values.AccountEarmark,
  Inflation: values.Inflation,
  IsTaxFree: values.IsTaxFree,
  OrionManagementStyleId: values.OrionManagementStyleId,
  CustomBucketIndex: values.CustomBucketIndex,
  OwnerModel: values.OwnerModel,
});
